import React from "react";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "../../../../components/Input/InputWrapper";
import {
  InputLabel,
  RequiredMark,
} from "../../../../components/Input/InputLabel";
import { CustomInput } from "../../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../../components/Input/CustomPhoneInput";
import {
  CountryAutoComplete,
  CountryAutoCompleteItem,
} from "../../../../components/Input/CountryAutoComplete";
import {
  FieldGrid,
  FieldGroup,
} from "../../../../components/OrgShell/FieldGroup";
import { InputError } from "../../../my-profile/InputError";
import { CustomInputChangeEvent } from "../../../../../utils/models/events/CustomInputChangeEvent";
import {
  FieldErrorMap,
  useVisibleFieldErrors,
} from "../../models/required-fields";

/** The shipping fields shared by the update form and the create wizard. */
export interface ShippingFieldsForm {
  shippingName?: string;
  shippingAddress?: string;
  shippingPostCode?: string;
  shippingCity?: string;
  shippingCountry?: string;
  shippingContactPerson?: string;
  shippingContactPhone?: string;
  shippingContactEmail?: string;
}

interface ShippingSectionProps {
  form: ShippingFieldsForm;
  onChange: (patch: Partial<ShippingFieldsForm>) => void;
  differs: boolean;
  onDiffersChange: (differs: boolean) => void;
  disabled?: boolean;
  /** Marks the fields required (asterisks) — customer screens only. */
  required?: boolean;
  /** Required-field errors by field name (empty when differs is off). */
  errors?: FieldErrorMap;
  /** Show every error (set after a blocked save/next attempt). */
  showErrors?: boolean;
}

export function ShippingSection({
  form,
  onChange,
  differs,
  onDiffersChange,
  disabled,
  required,
  errors,
  showErrors,
}: ShippingSectionProps) {
  const { t } = useTranslation();
  const { errorFor, markTouched, handleBlur } = useVisibleFieldErrors(
    errors,
    showErrors
  );
  const showRequired = !!required && differs && !disabled;

  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    onChange({ [target.name]: target.value });
  }

  function handleCountryChange(country: CountryAutoCompleteItem) {
    onChange({ shippingCountry: country.code });
  }

  function errorClass(field: string): string | undefined {
    return errorFor(field) ? "!border-red-100" : undefined;
  }

  return (
    <div className="space-y-6">
      <FieldGroup title={t("customerUpdate.shippingAddress")}>
        <label className="flex cursor-pointer items-center space-x-2">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={differs}
            onChange={(e) => onDiffersChange(e.target.checked)}
            disabled={disabled}
          />
          <span className="text-sm text-gray-700">
            {t("customerUpdate.shippingDiffersLabel")}
          </span>
        </label>
        {!differs && (
          <p className="ml-6 text-xs text-gray-500">
            {t("customerUpdate.shippedToCompanyAddress")}
          </p>
        )}
      </FieldGroup>

      {differs && (
        <FieldGrid>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.shippingName")}
              {showRequired && <RequiredMark />}
            </InputLabel>
            <CustomInput
              name="shippingName"
              value={form.shippingName}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("shippingName")}
            />
            <InputError errorMessage={errorFor("shippingName")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.shippingAddress")}
              {showRequired && <RequiredMark />}
            </InputLabel>
            <CustomInput
              name="shippingAddress"
              value={form.shippingAddress}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("shippingAddress")}
            />
            <InputError errorMessage={errorFor("shippingAddress")} />
          </InputWrapper>
          <div className="grid grid-cols-2 gap-x-4">
            <InputWrapper>
              <InputLabel>
                {t("customerUpdate.shippingPostcode")}
                {showRequired && <RequiredMark />}
              </InputLabel>
              <CustomInput
                name="shippingPostCode"
                value={form.shippingPostCode}
                onChange={handleInput}
                onBlur={handleBlur}
                disabled={disabled}
                className={errorClass("shippingPostCode")}
              />
              <InputError errorMessage={errorFor("shippingPostCode")} />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>
                {t("customerUpdate.shippingCity")}
                {showRequired && <RequiredMark />}
              </InputLabel>
              <CustomInput
                name="shippingCity"
                value={form.shippingCity}
                onChange={handleInput}
                onBlur={handleBlur}
                disabled={disabled}
                className={errorClass("shippingCity")}
              />
              <InputError errorMessage={errorFor("shippingCity")} />
            </InputWrapper>
          </div>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.shippingCountry")}
              {showRequired && <RequiredMark />}
            </InputLabel>
            <div
              data-field="shippingCountry"
              onBlur={() => markTouched("shippingCountry")}
            >
              <CountryAutoComplete
                disabled={!!disabled}
                selectedCode={form.shippingCountry}
                onCountryChange={handleCountryChange}
                error={!!errorFor("shippingCountry")}
              />
            </div>
            <InputError errorMessage={errorFor("shippingCountry")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.shippingContactPerson")}
              {showRequired && <RequiredMark />}
            </InputLabel>
            <CustomInput
              name="shippingContactPerson"
              value={form.shippingContactPerson}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("shippingContactPerson")}
            />
            <InputError errorMessage={errorFor("shippingContactPerson")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.shippingContactPhone")}
              {showRequired && <RequiredMark />}
            </InputLabel>
            <CustomPhoneInput
              name="shippingContactPhone"
              value={form.shippingContactPhone || ""}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={!!disabled}
              className={errorClass("shippingContactPhone")}
            />
            <InputError errorMessage={errorFor("shippingContactPhone")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.shippingContactEmail")}
              {showRequired && <RequiredMark />}
            </InputLabel>
            <CustomInput
              type="email"
              name="shippingContactEmail"
              value={form.shippingContactEmail}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("shippingContactEmail")}
            />
            <InputError errorMessage={errorFor("shippingContactEmail")} />
          </InputWrapper>
        </FieldGrid>
      )}
    </div>
  );
}
